import { supabase } from '@/shared/supabase/client'
import { resolveAuthUserAfterRefresh } from '@/shared/supabase/resolveAuthUserAfterRefresh'
import { getScreenshotPersona, isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import {
  FIXTURE_USER_SYSTEM_ADMIN_ID,
} from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import type { Database, Json } from '@/shared/supabase/database.types'

export interface ProfileRecord {
  id: string
  email: string
  role: 'doctor' | 'patient' | 'admin'
  isSystemAdmin: boolean
  displayName: string
  printName?: string
  contactEmail?: string
  phone1?: string
  phone2?: string
  avatarUrl?: string
  printLogoUrl?: string
  crm?: string
  specialties?: string[]
  personType?: 'pf' | 'pj'
  cpfCnpj?: string
  pis?: string
  tradeName?: string
  onboardingWelcomeSeen: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateProfilePayload {
  displayName?: string | null
  printName?: string | null
  contactEmail?: string | null
  phone1?: string | null
  phone2?: string | null
  avatarUrl?: string | null
  printLogoUrl?: string | null
  crm?: string | null
  specialties?: Json | null
  personType?: 'pf' | 'pj' | null
  cpfCnpj?: string | null
  pis?: string | null
  tradeName?: string | null
  role?: 'doctor' | 'patient' | 'admin'
}

const toProfileRecord = (row: {
  id: string
  email: string
  role: 'doctor' | 'patient' | 'admin'
  is_system_admin: boolean
  display_name: string
  print_name: string | null
  contact_email: string | null
  phone1: string | null
  phone2: string | null
  avatar_url: string | null
  print_logo_url: string | null
  crm: string | null
  specialties: unknown
  person_type: 'pf' | 'pj' | null
  cpf_cnpj: string | null
  pis: string | null
  trade_name: string | null
  onboarding_welcome_seen?: boolean
  created_at: string
  updated_at: string
}): ProfileRecord => ({
  id: row.id,
  email: row.email,
  role: row.role,
  isSystemAdmin: row.is_system_admin,
  displayName: row.display_name,
  printName: row.print_name ?? undefined,
  contactEmail: row.contact_email ?? undefined,
  phone1: row.phone1 ?? undefined,
  phone2: row.phone2 ?? undefined,
  avatarUrl: row.avatar_url ?? undefined,
  printLogoUrl: row.print_logo_url ?? undefined,
  crm: row.crm ?? undefined,
  specialties: Array.isArray(row.specialties)
    ? row.specialties.filter((item): item is string => typeof item === 'string')
    : undefined,
  personType: row.person_type ?? undefined,
  cpfCnpj: row.cpf_cnpj ?? undefined,
  pis: row.pis ?? undefined,
  tradeName: row.trade_name ?? undefined,
  onboardingWelcomeSeen: Boolean(row.onboarding_welcome_seen),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

/** Valida em tempo real no banco: profiles.is_system_admin para auth.uid(). */
const fetchIsSystemAdminFromDb = async (): Promise<boolean> => {
  if (isScreenshotFixtureMode()) {
    return getScreenshotPersona() === 'system_admin'
  }
  try {
    const { data, error } = await supabase.rpc('is_system_admin')
    if (error) return false
    return Boolean(data)
  } catch {
    return false
  }
}

/** Fallback para criar perfil quando não existe (evita 406 / "Cannot coerce"). */
const createProfileIfMissing = async (user: {
  id: string
  email?: string
  name?: string
  role?: string
}): Promise<ProfileRecord> => {
  const email = (user.email ?? `${user.id}@invalid.local`).trim() || `${user.id}@invalid.local`
  const displayName = user.name ?? email.split('@')[0] ?? 'Usuário'
  const role = (user.role === 'doctor' || user.role === 'admin' ? user.role : 'patient') as 'doctor' | 'patient' | 'admin'

  const { data, error } = await supabase
    .from('profiles')
    .insert({ id: user.id, email, display_name: displayName, role })
    .select('*')
    .single()

  if (error) throw toRepositoryError(error, 'Não foi possível criar o perfil.')
  return toProfileRecord(data)
}

const fetchProfileById = async (
  userId: string,
  createIfMissing?: { id: string; email?: string; name?: string; role?: string },
): Promise<{ profile: ProfileRecord }> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()

  if (error) {
    throw toRepositoryError(error, 'Não foi possível carregar o perfil.')
  }

  if (data) return { profile: toProfileRecord(data) }

  if (createIfMissing) {
    const profile = await createProfileIfMissing(createIfMissing)
    return { profile }
  }

  throw toRepositoryError(new Error('Perfil não encontrado.'), 'Não foi possível carregar o perfil.')
}

/**
 * Obtém o user ID autenticado. Usa getUser() (valida no servidor) para evitar
 * sessão em cache desatualizada após logout/login. getSession() pode retornar
 * dados em cache que não refletem o estado atual.
 */
const requireUserId = async (): Promise<string> => {
  const tryGetUser = async (): Promise<string> => {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    if (!data.user) throw new Error('Usuário não autenticado.')
    return data.user.id
  }

  try {
    return await tryGetUser()
  } catch (firstErr) {
    const msg = (firstErr instanceof Error ? firstErr.message : String(firstErr ?? '')).toLowerCase()
    const isSessionError =
      msg.includes('session') ||
      msg.includes('refresh') ||
      msg.includes('missing') ||
      msg.includes('auth') ||
      msg.includes('invalid') ||
      msg.includes('jwt') ||
      msg.includes('token') ||
      msg.includes('expired')
    if (isSessionError) {
      const { error: refreshErr } = await supabase.auth.refreshSession()
      if (!refreshErr) {
        return await resolveAuthUserAfterRefresh(
          tryGetUser,
          'Não foi possível validar a sessão. Faça login novamente.',
        )
      }
    }
    throw toRepositoryError(
      firstErr,
      'Não foi possível validar a sessão. Faça login novamente.',
      401,
    )
  }
}

export const profilesRepository = {
  /** Retorna profiles.is_system_admin do usuário logado (auth.uid()). Única fonte da verdade. */
  getIsSystemAdmin: async (): Promise<boolean> => fetchIsSystemAdminFromDb(),

  getIsSystemAdminForUser: async (userId: string): Promise<boolean> => {
    if (isScreenshotFixtureMode() && userId === FIXTURE_USER_SYSTEM_ADMIN_ID) {
      return true
    }
    if (isScreenshotFixtureMode()) {
      return false
    }
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_system_admin')
        .eq('id', userId)
        .maybeSingle()
      if (error) return false
      return Boolean(data?.is_system_admin)
    } catch {
      return false
    }
  },

  getCurrent: async (): Promise<{ profile: ProfileRecord }> => {
    try {
      const userId = await requireUserId()
      return fetchProfileById(userId)
    } catch (error) {
      throw toRepositoryError(error as Error, 'Falha ao obter perfil atual.')
    }
  },

  getProfileById: async (
    userId: string,
    createIfMissing?: { id: string; email?: string; name?: string; role?: string },
  ): Promise<{ profile: ProfileRecord }> => fetchProfileById(userId, createIfMissing),

  updateCurrent: async (payload: UpdateProfilePayload): Promise<{ profile: ProfileRecord }> => {
    try {
      const userId = await requireUserId()
      const data: Database['public']['Tables']['profiles']['Update'] = {}

      if (payload.displayName !== undefined && payload.displayName !== null) {
        data.display_name = payload.displayName
      }
      if (payload.printName !== undefined) data.print_name = payload.printName
      if (payload.contactEmail !== undefined) data.contact_email = payload.contactEmail
      if (payload.phone1 !== undefined) data.phone1 = payload.phone1
      if (payload.phone2 !== undefined) data.phone2 = payload.phone2
      if (payload.avatarUrl !== undefined) data.avatar_url = payload.avatarUrl
      if (payload.printLogoUrl !== undefined) data.print_logo_url = payload.printLogoUrl
      if (payload.crm !== undefined) data.crm = payload.crm
      if (payload.specialties !== undefined) data.specialties = payload.specialties
      if (payload.personType !== undefined) data.person_type = payload.personType
      if (payload.cpfCnpj !== undefined) data.cpf_cnpj = payload.cpfCnpj
      if (payload.pis !== undefined) data.pis = payload.pis
      if (payload.tradeName !== undefined) data.trade_name = payload.tradeName
      if (payload.role !== undefined) data.role = payload.role

      const { data: updated, error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', userId)
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível atualizar o perfil atual.')
      }

      return { profile: toProfileRecord(updated) }
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao atualizar perfil atual.')
    }
  },

  markWelcomeSeen: async (): Promise<void> => {
    const userId = await requireUserId()
    await supabase
      .from('profiles')
      .update({ onboarding_welcome_seen: true } as never)
      .eq('id', userId)
  },

  resetWelcomeForUser: async (targetUserId: string): Promise<void> => {
    await supabase
      .from('profiles')
      .update({ onboarding_welcome_seen: false } as never)
      .eq('id', targetUserId)
  },
}
